import { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import {
  UserIcon,
  ArrowRightOnRectangleIcon,
  ChevronDownIcon,
  BellIcon,
  CreditCardIcon,
  QuestionMarkCircleIcon,
  StarIcon,
  SparklesIcon,
  BuildingOfficeIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline'

interface ProfileDropdownProps {
  compact?: boolean
}

const ProfileDropdown = ({ compact = false }: ProfileDropdownProps) => {
  const { user, logout, isAdmin } = useAuth()
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 })

  // Close dropdown when clicking outside and handle scroll lock
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    const handleScroll = () => {
      if (isOpen) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleEscape)
      document.addEventListener('scroll', handleScroll, true) // Use capture to catch all scroll events
      // Prevent body scroll when dropdown is open
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
      document.removeEventListener('scroll', handleScroll, true)
      // Restore body scroll
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  // Calculate dropdown position
  const calculatePosition = () => {
    if (!buttonRef.current) return
    
    const rect = buttonRef.current.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    const viewportWidth = window.innerWidth
    const dropdownWidth = 256 // 16rem = 256px (w-64)
    const dropdownHeight = 400 // Approximate dropdown height
    const padding = 16 // Minimum padding from viewport edges
    
    // Calculate horizontal position
    let right = viewportWidth - rect.right
    
    // Ensure dropdown doesn't go off the left edge
    if (right + dropdownWidth > viewportWidth - padding) {
      right = padding
    }
    
    // Ensure dropdown doesn't go off the right edge
    if (right < padding) {
      right = padding
    }
    
    // Calculate vertical position
    let top = rect.bottom + 8
    
    // Check if dropdown would go off-screen vertically
    if (top + dropdownHeight > viewportHeight - padding) {
      top = rect.top - dropdownHeight - 8 // Position above button if no space below
    }
    
    // Ensure minimum padding from top and bottom
    top = Math.max(padding, Math.min(top, viewportHeight - dropdownHeight - padding))
    
    setDropdownPosition({
      top,
      right
    })
  }

  // Calculate dropdown position when opened and on window resize
  useEffect(() => {
    if (isOpen) {
      calculatePosition()
      
      // Recalculate on window resize
      const handleResize = () => {
        if (isOpen) {
          calculatePosition()
        }
      }
      
      window.addEventListener('resize', handleResize)
      return () => window.removeEventListener('resize', handleResize)
    }
  }, [isOpen])

  const menuItems = [
    {
      icon: UserIcon,
      label: 'Profile Settings',
      action: () => {
        navigate('/profile')
        setIsOpen(false)
      }
    },
    {
      icon: BellIcon,
      label: 'Notifications',
      action: () => {
        navigate('/profile?tab=notifications')
        setIsOpen(false)
      }
    },
    {
      icon: CreditCardIcon,
      label: 'Billing & Subscription',
      action: () => {
        navigate('/upgrade')
        setIsOpen(false)
      }
    },
    ...(isAdmin() ? [{
      icon: ShieldCheckIcon,
      label: 'Admin Panel',
      action: () => {
        navigate('/admin')
        setIsOpen(false)
      }
    }] : []),
    {
      icon: QuestionMarkCircleIcon,
      label: 'Help & Support',
      action: () => {
        window.open('mailto:support@shootsuite.com', '_blank')
        setIsOpen(false)
      }
    }
  ]

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Profile Button */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center p-2 rounded-xl hover:bg-white/10 transition-all group touch-manipulation ${
          compact ? 'space-x-1' : 'space-x-2 sm:space-x-3'
        }`}
      >
        {!compact && (
          <div className="text-right hidden sm:block">
            <p className="text-white font-medium text-sm group-hover:text-gray-200 transition-colors">
              {user?.firstName} {user?.lastName}
            </p>
            <p className="text-gray-400 text-xs group-hover:text-gray-300 transition-colors">
              {user?.businessName || user?.email}
            </p>
          </div>
        )}
        
        {/* Profile Picture with Plan Border and Icon */}
        <div className="relative">
          {/* Plan Border */}
          <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full p-0.5 ${
            user?.subscriptionStatus === 'professional' 
              ? 'bg-gradient-to-r from-yellow-400 to-orange-500' 
              : user?.subscriptionStatus === 'studio'
              ? 'bg-gradient-to-r from-purple-500 to-pink-600'
              : 'bg-gradient-to-r from-emerald-500 to-teal-600'
          }`}>
            {user?.profilePicture ? (
              <img
                src={user.profilePicture}
                alt="Profile"
                className="w-full h-full rounded-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xs sm:text-sm">
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </div>
            )}
          </div>
          
          {/* Plan Icon Bubble */}
          <div className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 sm:w-5 sm:h-5 rounded-full flex items-center justify-center shadow-lg ${
            user?.subscriptionStatus === 'professional' 
              ? 'bg-gradient-to-r from-yellow-400 to-orange-500' 
              : user?.subscriptionStatus === 'studio'
              ? 'bg-gradient-to-r from-purple-500 to-pink-600'
              : 'bg-gradient-to-r from-emerald-500 to-teal-600'
          }`}>
            {user?.subscriptionStatus === 'professional' ? (
              <StarIcon className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-black" />
            ) : user?.subscriptionStatus === 'studio' ? (
              <BuildingOfficeIcon className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" />
            ) : (
              <SparklesIcon className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" />
            )}
          </div>
        </div>
        {!compact && (
          <ChevronDownIcon 
            className={`w-4 h-4 text-gray-400 transition-transform duration-200 hidden sm:block ${
              isOpen ? 'rotate-180' : ''
            }`} 
          />
        )}
      </button>

      {/* Dropdown Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed w-64 sm:w-72 rounded-2xl overflow-hidden z-[9999] border border-white/30 profile-dropdown-menu"
            style={{
              background: 'rgba(0, 0, 0, 0.95)',
              backdropFilter: 'blur(20px)',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.9), 0 0 0 1px rgba(255, 255, 255, 0.2)',
              top: `${dropdownPosition.top}px`,
              right: `${dropdownPosition.right}px`
            }}
          >
            {/* User Info Header */}
            <div className="p-4 border-b border-white/10">
              <div className="flex items-center space-x-3">
                {user?.profilePicture ? (
                  <img
                    src={user.profilePicture}
                    alt="Profile"
                    className="w-12 h-12 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                    {user?.firstName?.[0]}{user?.lastName?.[0]}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium truncate">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-gray-400 text-sm truncate">{user?.businessName || user?.email}</p>
                  <div className="flex items-center mt-1">
                    <div className={`w-2 h-2 rounded-full mr-2 ${
                      user?.subscriptionStatus === 'professional' 
                        ? 'bg-gradient-to-r from-yellow-400 to-orange-500' 
                        : user?.subscriptionStatus === 'studio'
                        ? 'bg-gradient-to-r from-purple-500 to-pink-600'
                        : 'bg-gradient-to-r from-emerald-500 to-teal-600'
                    }`}></div>
                    <span className="text-xs text-gray-400 capitalize">
                      {user?.subscriptionStatus || 'freemium'} plan
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Menu Items */}
            <div className="p-2 profile-dropdown-content">
              {menuItems.map((item) => (
                <button
                  key={item.label}
                  onClick={item.action}
                  className="w-full flex items-center space-x-3 p-3 rounded-xl hover:bg-white/10 transition-colors text-left group touch-manipulation"
                >
                  <item.icon className="w-5 h-5 text-gray-400 group-hover:text-white transition-colors flex-shrink-0" />
                  <span className="text-gray-300 group-hover:text-white transition-colors text-sm">
                    {item.label}
                  </span>
                </button>
              ))}
              
              {/* Divider */}
              <div className="h-px bg-white/10 my-2"></div>
              
              {/* Sign Out */}
              <button
                onClick={() => {
                  logout()
                  setIsOpen(false)
                }}
                className="w-full flex items-center space-x-3 p-3 rounded-xl hover:bg-red-500/20 transition-colors text-left group touch-manipulation"
              >
                <ArrowRightOnRectangleIcon className="w-5 h-5 text-gray-400 group-hover:text-red-400 transition-colors flex-shrink-0" />
                <span className="text-gray-300 group-hover:text-red-400 transition-colors text-sm">
                  Sign Out
                </span>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default ProfileDropdown 